import React, { useState, useMemo } from "react";
import { DriverScores } from "../../../../../api/apiTypes";

interface IntermediarioRow {
  intermediario: string;
  values: number[];
}

interface TabellaAnaliticaProps {
  driverScores?: DriverScores;
}

const colonne: { key: keyof DriverScores; label: string }[] = [
  { key: "Adesione mercati - Italia", label: "Adesione mercati italiani" },
  { key: "Adesione mercati - Estero", label: "Adesione mercati esteri" },
  {
    key: "Strategia di Best Execution - Mercati Azionari",
    label: "Best execution mercati azionari",
  },
  {
    key: "Strategia di Best Execution - Mercati Obbligazionari",
    label: "Best execution mercati obbligazionari",
  },
  {
    key: "Strategia di Best Execution - Italia",
    label: "Best execution - Italia",
  },
  {
    key: "Strategia di Best Execution - Estero",
    label: "Best execution - Estero",
  },
  {
    key: "Indici di penetrazione mercati - Cash",
    label: "Indice di penetrazione mercati cash",
  },
  {
    key: "Indici di penetrazione mercati - Derivati",
    label: "Indice di penetrazione mercati derivati",
  },
  { key: "Trasparenza Informativa", label: "Trasparenza informativa" },
  { key: "Indice di solidità", label: "Solidità" },
  { key: "Reclami", label: "Reclami" },
  { key: "Market Maker", label: "Market maker" },
  { key: "Trasparenza ESG", label: "Trasparenza esg" },
];

const TabellaAnalitica: React.FC<TabellaAnaliticaProps> = ({ driverScores }) => {
  const [sortColumn, setSortColumn] = useState<number | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const data: IntermediarioRow[] = useMemo(() => {
    if (!driverScores) return [];

    // ricaviamo la lista unica di intermediari
    const allBanks = new Set<string>();
    colonne.forEach(({ key }) => {
      Object.keys(driverScores[key] || {}).forEach((bank) =>
        allBanks.add(bank)
      );
    });

    // per ogni intermediario, estraiamo i valori per ogni colonna
    return Array.from(allBanks).map((bank) => {
      const values = colonne.map(({ key }) => {
        return driverScores[key]?.[bank] ?? 0;
      });
      return { intermediario: bank, values };
    });
  }, [driverScores]);

  // ordinamento
  const sortedData = useMemo(() => {
    if (sortColumn === null) return data;
    return [...data].sort((a, b) => {
      const valA = a.values[sortColumn];
      const valB = b.values[sortColumn];
      return sortDirection === "asc" ? valA - valB : valB - valA;
    });
  }, [data, sortColumn, sortDirection]);

  const handleSort = (index: number) => {
    if (sortColumn === index) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(index);
      setSortDirection("asc");
    }
  };

  return (
    <div>
      <h2 className="text-xl font-bold mb-4 pl-2">
        TABELLA ANALITICA DEI PUNTEGGI
      </h2>
      <div className="overflow-x-auto bg-surface rounded-lg shadow-md">
        <table className="min-w-full border-collapse">
          <thead className="bg-gray-50 text-xs text-textSecondary font-semibold border-b border-divider">
            <tr>
              <th className="p-2 text-left">Intermediario</th>
              {colonne.map((col, i) => (
                <th
                  key={i}
                  className="p-2 text-center cursor-pointer"
                  onClick={() => handleSort(i)}
                >
                  {col.label}{" "}
                  {sortColumn === i && (sortDirection === "asc" ? "▲" : "▼")}
                </th>
              ))}
              <th className="p-2 text-center">Totale</th>
            </tr>
          </thead>
          <tbody className="text-xs text-textPrimary">
            {sortedData.map((row, idx) => {
              const total = row.values.reduce((sum, v) => sum + v, 0);
              return (
                <tr key={idx} className={idx % 2 === 0 ? "bg-gray-50" : ""}>
                  <td className="p-4 font-medium">{row.intermediario}</td>
                  {row.values.map((val, i) => (
                    <td key={i} className="p-4 text-center">
                      {val}
                    </td>
                  ))}
                  <td className="p-4 text-center font-semibold">{total}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TabellaAnalitica;
